import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X, FileText, FilePlus, ChevronRight, AlertTriangle } from 'lucide-react';
import type { ProposalRecord, ProposalStatus } from '@cortex/shared';
import { Api } from '../../lib/api';
import { useUi } from '../../store/ui';
import { qk } from '../../hooks/useVault';
import { useSessionContext } from '../../hooks/sessionContext';

export function useProposals(status: ProposalStatus | undefined = 'PENDING') {
  return useQuery({ queryKey: ['proposals', status], queryFn: () => Api.agent.proposals(status), refetchInterval: 30_000, staleTime: 10_000 });
}

export function ProposalsPanel() {
  const [status, setStatus] = useState<ProposalStatus>('PENDING');
  const q = useProposals(status);
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <div className="proposals">
      <div className="proposals-head">
        <div className="modes">
          {(['PENDING', 'ACCEPTED', 'REJECTED', 'STALE'] as ProposalStatus[]).map((s) => (
            <button key={s} className={status === s ? 'on' : ''} onClick={() => (setStatus(s), setOpenId(null))}>{s.toLowerCase()}</button>
          ))}
        </div>
      </div>
      {q.data?.proposals.length === 0 && <div className="dock-empty">No {status.toLowerCase()} proposals. The agent proposes; you decide.</div>}
      {q.data?.proposals.map((p) => (
        <ProposalRow key={p.id} p={p} open={openId === p.id} onToggle={() => setOpenId(openId === p.id ? null : p.id)} />
      ))}
    </div>
  );
}

function ProposalRow({ p, open, onToggle }: { p: ProposalRecord; open: boolean; onToggle: () => void }) {
  const qc = useQueryClient();
  const toast = useUi((s) => s.toast);
  const openNote = useUi((s) => s.openNote);
  const session = useSessionContext();
  const detail = useQuery({ queryKey: ['proposal', p.id], queryFn: () => Api.agent.proposal(p.id), enabled: open, staleTime: 5_000 });
  const hunks = useMemo(() => (p.diff ? splitHunks(p.diff) : []), [p.diff]);
  const [selected, setSelected] = useState<Set<number>>(new Set(hunks.map((_, i) => i)));
  const canDecide = p.status === 'PENDING' && session.user?.role !== 'VIEWER';

  const accept = async () => {
    try {
      const all = selected.size === hunks.length;
      const r = await Api.agent.acceptProposal(p.id, p.kind === 'patch' && !all ? [...selected].sort() : undefined);
      toast(`Accepted → ${r.path}`);
      qc.invalidateQueries({ queryKey: ['proposals'] });
      qc.invalidateQueries({ queryKey: qk.tree });
      qc.invalidateQueries({ queryKey: qk.note(r.noteId) });
      openNote(r.noteId);
    } catch (err) {
      const e = err as { message: string; body?: { status?: string } };
      toast(e.body?.status === 'STALE' ? 'Note changed since this was proposed: marked stale' : e.message, 'crit');
      qc.invalidateQueries({ queryKey: ['proposals'] });
    }
  };
  const reject = async () => {
    await Api.agent.rejectProposal(p.id);
    qc.invalidateQueries({ queryKey: ['proposals'] });
  };

  return (
    <div className={`proposal ${p.status.toLowerCase()}`}>
      <button className="proposal-head" onClick={onToggle}>
        <ChevronRight size={12} className={`chev${open ? ' open' : ''}`} />
        {p.kind === 'note' ? <FilePlus size={13} /> : <FileText size={13} />}
        <span className="t">{p.title}</span>
        <span className="mono faint path">{p.path}</span>
      </button>
      {open && (
        <div className="proposal-body">
          {p.reason && <div className="reason">{p.reason}</div>}
          {detail.data?.stale && p.status === 'PENDING' && (
            <div className="pane-banner"><AlertTriangle size={13} /> The note has changed since this was proposed. Accepting will fail; ask the agent again.</div>
          )}
          {p.kind === 'note' ? (
            <pre className="mono diff new">{p.content}</pre>
          ) : (
            <div className="hunks">
              {hunks.map((h, i) => (
                <div key={i} className={`hunk${selected.has(i) ? '' : ' off'}`}>
                  {canDecide && hunks.length > 1 && (
                    <label className="hunk-pick">
                      <input type="checkbox" checked={selected.has(i)} onChange={(e) => { const n = new Set(selected); e.target.checked ? n.add(i) : n.delete(i); setSelected(n); }} /> hunk {i + 1}
                    </label>
                  )}
                  <pre className="mono diff">{h.split('\n').map((l, j) => <span key={j} className={l.startsWith('+') ? 'add' : l.startsWith('-') ? 'del' : l.startsWith('@@') ? 'hdr' : ''}>{l}{'\n'}</span>)}</pre>
                </div>
              ))}
            </div>
          )}
          {canDecide && (
            <div className="proposal-actions">
              <button className="primary" onClick={() => void accept()} disabled={p.kind === 'patch' && selected.size === 0}><Check size={12} /> Accept{p.kind === 'patch' && selected.size < hunks.length ? ` ${selected.size}/${hunks.length}` : ''}</button>
              <button className="danger" onClick={() => void reject()}><X size={12} /> Reject</button>
              {p.noteId && <button className="ghost" onClick={() => openNote(p.noteId!)}>Open note</button>}
            </div>
          )}
          <div className="faint mono" style={{ fontSize: 10 }}>{p.status.toLowerCase()} · {new Date(p.createdAt).toLocaleString()}</div>
        </div>
      )}
    </div>
  );
}

function splitHunks(diff: string): string[] {
  const lines = diff.split('\n');
  const out: string[] = [];
  let cur: string[] | null = null;
  for (const l of lines) {
    if (l.startsWith('@@')) {
      if (cur) out.push(cur.join('\n'));
      cur = [l];
    } else if (cur) cur.push(l);
  }
  if (cur) out.push(cur.join('\n'));
  return out;
}
